import {useCallback} from 'react';

import {getCurrentFirebaseUser} from '#hook/useFirebase';

export enum PersistentTokenName {
  AuthenticationToken = 'authentication-token',
  UserIdToken = 'user-id-token',
}

type SetPersistentTokenValueOptions = {
  /**
   * The amount of time (in milliseconds) after which the token would expire.
   */
  expiry?: number;
};

type PersistedToken = {
  time: number;
  value: string;
  options?: SetPersistentTokenValueOptions;
};

const NAMESPACE = 'tally-health';

const withNamespace = (str: string) => `${NAMESPACE}:${str}`;

const encodeTokenValue = (value: string, options?: SetPersistentTokenValueOptions) => {
  const persistedToken: PersistedToken = {
    time: new Date().getTime(),
    value,
    options: options,
  };

  return JSON.stringify(persistedToken);
};

const decodeTokenValue = (value: string): string | null => {
  let persistedToken: PersistedToken | null;

  try {
    persistedToken = JSON.parse(value) as PersistedToken;
  } catch {
    persistedToken = null;
  }

  if (!persistedToken) {
    return null;
  } else {
    const tokenTime = persistedToken.time;
    const tokenExpiry = persistedToken.options?.expiry;

    if (tokenTime && tokenTime > 0 && tokenExpiry && tokenExpiry > 0) {
      if (tokenTime + tokenExpiry >= new Date().getTime()) {
        return persistedToken.value || null;
      } else {
        return null;
      }
    } else {
      return persistedToken.value || null;
    }
  }
};

export const getPersistentTokenValue = async (
  name: PersistentTokenName,
): Promise<string | null> => {
  if (name === PersistentTokenName.AuthenticationToken) {
    // Firebase authentication token is maintained by the Firebase FE client.
    // So instead of reading/parsing the token from localstorage, we just read
    // it directly from the Firebase FE library.
    const firebaseUser = await getCurrentFirebaseUser();
    if (firebaseUser) {
      // By calling `getIdToken` on the Firebase user, the client side Firebase
      // library will also automatically refresh the token for us (if the token
      // needed refreshing).
      return await firebaseUser.getIdToken();
    } else {
      return null;
    }
  } else {
    const storageItem = localStorage.getItem(withNamespace(name));

    if (storageItem) {
      const decodedValue = decodeTokenValue(storageItem);
      if (!decodedValue) {
        await removePersistentTokenValue(name);
      }
      return decodedValue;
    } else {
      return null;
    }
  }
};

export const setPersistentTokenValue = async (
  name: PersistentTokenName,
  value: string,
  options?: SetPersistentTokenValueOptions,
): Promise<void> => {
  if (name === PersistentTokenName.AuthenticationToken) {
    // Firebase authentication token is maintained by the Firebase FE client.
    // So we just do nothing here because there is no need to persist it again.
    // And the `getPersistentTokenValue` will also read the token value
    // directly from the Firebase FE library.
    return;
  } else {
    localStorage.setItem(withNamespace(name), encodeTokenValue(value, options));
  }
};

export const removePersistentTokenValue = async (name: PersistentTokenName): Promise<void> => {
  if (name === PersistentTokenName.AuthenticationToken) {
    // Firebase authentication token is maintained by the Firebase FE client.
    // So we just do nothing here because there is no need to remove it again.
    return;
  } else {
    localStorage.removeItem(withNamespace(name));
  }
};

export const usePersistentToken = (
  name: PersistentTokenName,
): {
  getToken: () => Promise<string | null>;
  setToken: (value: string, options?: SetPersistentTokenValueOptions) => Promise<void>;
  removeToken: () => Promise<void>;
} => {
  const handleGetToken = useCallback(async () => await getPersistentTokenValue(name), [name]);

  const handleSetToken = useCallback(
    async (value: string, options?: SetPersistentTokenValueOptions) =>
      await setPersistentTokenValue(name, value, options),
    [name],
  );

  const handleRemoveToken = useCallback(async () => await removePersistentTokenValue(name), [name]);

  return {
    getToken: handleGetToken,
    setToken: handleSetToken,
    removeToken: handleRemoveToken,
  };
};
